import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { EntityLink as EntityLinkBase } from '../../types/outreach';
import { parseDistricts, formatDistricts as sharedFormatDistricts } from '../../utils/districts';
import { Mail, User, MapPin, ExternalLink, Loader2, CheckCircle, Send, Copy, ArrowLeft, Lock, Users, Building2, UserCheck } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatLegislatorNameFromObject } from '../../utils/nameUtils';
import type { RecipientType, CustomRecipient, SelectedCommittee } from '../../types/outreach';
import { EditIcon } from '../icons';
import { padDistrictNumber } from '../../utils/districtDisplay';

interface EmailTemplateSection {
  content: string;
  /** @deprecated Ignored — the mail client makes the final message editable anyway */
  respondent_editable?: boolean;
}

interface EmailTemplate {
  subject: EmailTemplateSection;
  opening: EmailTemplateSection;
  included_question_ids?: string[];
  free_text_enabled: boolean;
  free_text_placeholder?: string;
  closing: EmailTemplateSection;
}

interface RecipientContactConfig {
  enabled: boolean;
  /** @deprecated No longer written or read. Retained so live definitions still type-check. */
  heading?: string;
  /** @deprecated No longer written or read. Retained so live definitions still type-check. */
  subheading?: string;
  recipient_types?: RecipientType[];
  custom_recipients?: CustomRecipient[];
  /** Committees picked explicitly; used when recipient_types includes 'committee' */
  committees?: SelectedCommittee[];
  /** 'bcc' (default) hides recipients from each other; 'to' makes them visible */
  recipient_visibility?: 'bcc' | 'to';
  /** Append an "About the respondent" block to the email body */
  include_respondent_info?: boolean;
  respondent_info_fields?: string[];
  email_template?: EmailTemplate;
  message_template?: string;
  subject_template?: string;
  contact_methods?: string[];
  show_after_submit?: boolean;
}

// Canonical link shape plus the row id present on API-loaded links
interface EntityLink extends EntityLinkBase {
  id: string;
}

interface Recipient {
  id: string;
  name: string;
  email: string;
  source: string;
  chamber?: string;
  district?: string;
  party?: string;
  committee_name?: string;
}

interface Question {
  id: string;
  type: string;
  question: string;
  options?: { value: string; label: string }[];
}

interface Props {
  itemId: string;
  sessionId: string;
  contactConfig: RecipientContactConfig;
  demographics: RespondentDemographics;
  links: EntityLink[];
  answers?: Record<string, string | string[]>;
  questions?: Question[];
  primaryColor?: string;
  /** Test-mode session — skip engagement-event logging */
  isTest?: boolean;
  /**
   * The stored response this card follows (returned by the respond endpoint).
   * When present, the free-text personal message is saved onto it whenever a
   * send/copy action fires. Absent on flows with no response row (the inline
   * contact-only card) — then nothing is stored, by design.
   */
  responseId?: string;
  /** Collector slug — the personal-message save endpoint is slug-scoped. */
  slug?: string;
  /** For the disclosure line under the free-text box. */
  organizationName?: string;
  /**
   * Overrides the card's header. The post-submission view passes "Recipients",
   * since there the card sits behind an "Email Selected Contacts" button and the
   * header only needs to label the list beneath it. Flows where this card IS the
   * page (message-only step 2) omit it and get the default heading plus a
   * recipient-count line.
   */
  title?: string;
}

type SendMethod = 'gmail' | 'mailto' | 'copied' | null;

const API_BASE = '/api';

function processTemplate(
  template: string,
  vars: Record<string, string>
): string {
  let result = template;

  for (const [key, val] of Object.entries(vars)) {
    result = result.replace(new RegExp(`\\{\\{${key.replace('.', '\\.')}\\}\\}`, 'g'), val);
  }

  result = result.replace(/\{\{#if\s+[\w.]+\}\}([\s\S]*?)\{\{\/if\}\}/g, (_match, content) => {
    const inner = content.replace(/\{\{([\w.]+)\}\}/g, (_m: string, key: string) => vars[key] || '');
    const hasContent = inner.trim().length > 0;
    return hasContent ? inner : '';
  });

  result = result.replace(/\{\{[\w.#/]+\}\}/g, '');

  return result.trim();
}

function buildTemplateVars(
  recipients: Recipient[],
  demographics: RespondentDemographics,
  links: EntityLink[],
  answers?: Record<string, string | string[]>,
  questions?: Question[]
): Record<string, string> {
  const billLinks = links.filter(l => l.entity_type === 'bill');
  let billNumber = '';
  let billTitle = '';

  if (billLinks.length === 1) {
    const displayName = billLinks[0].entity_display_name || '';
    const dashIdx = displayName.indexOf(' - ');
    billNumber = dashIdx > -1 ? displayName.substring(0, dashIdx) : displayName;
    billTitle = dashIdx > -1 ? displayName.substring(dashIdx + 3) : displayName;
  } else if (billLinks.length > 1) {
    const parts = billLinks.map(bl => {
      const dn = bl.entity_display_name || '';
      const di = dn.indexOf(' - ');
      return di > -1 ? dn.substring(0, di) : dn;
    });
    billNumber = parts.join(' and ');
    billTitle = billLinks.map(bl => {
      const dn = bl.entity_display_name || '';
      const di = dn.indexOf(' - ');
      return di > -1 ? dn.substring(di + 3) : dn;
    }).join('; ');
  }

  const legislatorName = recipients.length === 1 ? recipients[0].name : 'Elected Official';

  let positionValue = '';
  if (answers && questions) {
    const posQ = questions.find(q =>
      q.type === 'position' ||
      q.question.toLowerCase().includes('position') ||
      q.question.toLowerCase().includes('support or oppose')
    );
    if (posQ && answers[posQ.id]) {
      const raw = answers[posQ.id];
      const values = Array.isArray(raw) ? raw : [raw];
      positionValue = values
        .map(v => posQ.options?.find(o => o.value === v)?.label || v)
        .join(', ');
    }
  }

  const personalMessage = answers
    ? Object.entries(answers).find(([key]) => {
        const q = questions?.find(qq => qq.id === key);
        return q?.type === 'long_text' || q?.type === 'paragraph';
      })?.[1] as string || ''
    : '';

  return {
    // {{recipient.*}} is the documented token; {{legislator.*}} is the legacy
    // alias kept working forever (live templates contain it)
    'recipient.name': legislatorName,
    'legislator.name': legislatorName,
    'respondent.first_name': String(demographics.first_name ?? ''),
    'respondent.last_name': String(demographics.last_name ?? ''),
    // `address` is written by the district lookup from the geocoded result.
    // city / state / zip used to sit here too, but nothing ever populated them:
    // the lookup writes only `district` and `address`, and none of the three is
    // a collectable field. {{respondent.city}} and {{respondent.zip}} always
    // rendered empty, and {{respondent.state}} always rendered a hardcoded "CO"
    // regardless of where the respondent actually lived. Removed rather than
    // left fabricating a value — unknown placeholders are stripped downstream,
    // so a live template still referencing them renders nothing.
    'respondent.address': String(demographics.address ?? ''),
    'respondent.email': String(demographics.email ?? ''),
    'respondent.phone': String(demographics.phone ?? ''),
    'respondent.age_range': String(demographics.age_range ?? ''),
    'respondent.party_affiliation': String(demographics.party_affiliation ?? ''),
    'respondent.gender': String(demographics.gender ?? ''),
    'respondent.personal_message': personalMessage,
    'bill.number': billNumber,
    'bill.title': billTitle,
    'position': positionValue,
  };
}

// Fields available for the "About the respondent" email block
export const RESPONDENT_INFO_FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
  { value: 'district', label: 'District(s)' },
  { value: 'age_range', label: 'Age Range' },
  { value: 'party_affiliation', label: 'Party Affiliation' },
  { value: 'gender', label: 'Gender' },
];

/**
 * Demographic answers as collected. Every value is a string except `district`,
 * which is the five-part object from the address lookup — and older responses
 * hold that object stringified, which is why reads go through parseDistricts.
 */
export type RespondentDemographics = Record<string, unknown>;

export const DEFAULT_RESPONDENT_INFO_FIELDS = ['name', 'address', 'district'];

function formatDistricts(districtJson: unknown): string {
  // Object or stringified — one place understands both (utils/districts).
  return sharedFormatDistricts(districtJson);
}

function formatRespondentInfo(
  fields: string[],
  demographics: RespondentDemographics
): string {
  const lines: string[] = [];
  for (const field of fields) {
    let value = '';
    const label = RESPONDENT_INFO_FIELD_OPTIONS.find(o => o.value === field)?.label || field;
    if (field === 'name') {
      value = [demographics.first_name, demographics.last_name]
        .filter(Boolean)
        .map((part) => String(part))
        .join(' ');
    } else if (field === 'district') {
      value = demographics.district ? formatDistricts(demographics.district) : '';
    } else if (field === 'address') {
      value = [demographics.address, demographics.city, demographics.state, demographics.zip]
        .filter(Boolean)
        .map((part) => String(part))
        .join(', ');
    } else {
      value = String(demographics[field] ?? '');
    }
    if (value) lines.push(`${label}: ${value}`);
  }
  if (lines.length === 0) return '';
  return `About the respondent:\n${lines.join('\n')}`;
}

function resolveAnswerDisplay(
  raw: string | string[],
  question: Question
): string {
  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .map(v => question.options?.find(o => o.value === v)?.label || v)
    .join(', ');
}

function formatQuestionAnswers(
  questionIds: string[],
  answers: Record<string, string | string[]>,
  questions: Question[]
): string {
  return questionIds
    .map(id => {
      const q = questions.find(qq => qq.id === id);
      if (!q || !answers[id]) return null;
      return `${q.question}: ${resolveAnswerDisplay(answers[id], q)}`;
    })
    .filter(Boolean)
    .join('\n');
}

const SOURCE_LABELS: Record<string, { label: string; icon: React.FC<{ className?: string }> }> = {
  house_rep: { label: 'Your House Representative', icon: Building2 },
  senate_rep: { label: 'Your Senate Representative', icon: Building2 },
  committee_members: { label: 'Committee Members', icon: Users },
  committee: { label: 'Selected Committees', icon: Users },
  candidate: { label: 'Related Candidate', icon: UserCheck },
  custom: { label: 'Recipients', icon: User },
};

const RecipientContactCard: React.FC<Props> = ({
  itemId,
  sessionId,
  contactConfig,
  demographics,
  links,
  answers,
  questions,
  primaryColor = '#2563eb',
  isTest = false,
  responseId,
  slug,
  organizationName,
  title
}) => {
  const useStructured = !!contactConfig.email_template;
  const recipientTypes = useMemo(
    () => contactConfig.recipient_types || ['house_rep', 'senate_rep'],
    [contactConfig.recipient_types]
  );
  const selectedCommittees = useMemo(
    () => contactConfig.committees || [],
    [contactConfig.committees]
  );

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendMethod, setSendMethod] = useState<SendMethod>(null);

  // Legacy state
  const [legacyMessage, setLegacyMessage] = useState('');
  const [legacySubject, setLegacySubject] = useState('');
  const [showLegacyEditor, setShowLegacyEditor] = useState(false);

  // Structured state
  const [subjectText, setSubjectText] = useState('');
  const [openingText, setOpeningText] = useState('');
  const [questionAnswersText, setQuestionAnswersText] = useState('');
  const [freeText, setFreeText] = useState('');
  const [closingText, setClosingText] = useState('');

  // Resolve all recipients from configured sources
  useEffect(() => {
    if (!contactConfig.enabled) { setLoading(false); return; }

    const resolveRecipients = async () => {
      setLoading(true);
      const allRecipients: Recipient[] = [];
      const seenEmails = new Set<string>();

      const addRecipient = (r: Recipient) => {
        const email = r.email?.toLowerCase();
        if (email && !seenEmails.has(email)) {
          seenEmails.add(email);
          allRecipients.push(r);
        }
      };

      try {
        // District-based legislators (house_rep / senate_rep)
        if (recipientTypes.includes('house_rep') || recipientTypes.includes('senate_rep')) {
          const districtRaw = demographics.district;
          if (districtRaw) {
            try {
              // Object or stringified — parseDistricts takes either.
              const districtData = (parseDistricts(districtRaw) || {}) as Record<string, string>;
              const queries: { chamber: string; district: string; source: string }[] = [];

              if (recipientTypes.includes('senate_rep') && districtData.state_senate) {
                const num = districtData.state_senate.replace(/^SD-/, '');
                if (num) queries.push({ chamber: 'Senate', district: num, source: 'senate_rep' });
              }
              if (recipientTypes.includes('house_rep') && districtData.state_house) {
                const num = districtData.state_house.replace(/^HD-/, '');
                if (num) queries.push({ chamber: 'House', district: num, source: 'house_rep' });
              }

              const results = await Promise.all(
                queries.map(q =>
                  axios.get(`${API_BASE}/legislators/public/by-district`, { params: { chamber: q.chamber, district: q.district } })
                    .then(r => (r.data.data?.legislators || []).map((leg: any) => ({
                      id: leg.id,
                      name: formatLegislatorNameFromObject(leg) || leg.fullName || `${leg.firstName} ${leg.lastName}`,
                      email: leg.email,
                      source: q.source,
                      chamber: leg.chamber,
                      district: leg.district,
                      party: leg.partyAffiliation
                    })))
                    .catch(() => [])
                )
              );
              results.flat().forEach(addRecipient);
            } catch { /* invalid district JSON */ }
          }
        }

        // Committee members from all related bills
        if (recipientTypes.includes('committee_members')) {
          const billLinks = links.filter(l => l.entity_type === 'bill');
          const results = await Promise.all(
            billLinks.map(bl => {
              const dn = bl.entity_display_name || '';
              const dashIdx = dn.indexOf(' - ');
              const billNumber = dashIdx > -1 ? dn.substring(0, dashIdx).trim() : dn.trim();
              return axios.get(`${API_BASE}/bills/public/${encodeURIComponent(billNumber)}/committee-members`)
                .then(r => (r.data.data?.members || []).map((m: any) => ({
                  id: m.id,
                  name: m.name,
                  email: m.email,
                  source: 'committee_members',
                  chamber: m.chamber,
                  district: m.district,
                  party: m.party,
                  committee_name: m.committee_name
                })))
                .catch(() => []);
            })
          );
          results.flat().forEach(addRecipient);
        }

        // Members of committees the creator picked explicitly. Resolved live so a
        // roster change since the item was built is picked up automatically.
        if (recipientTypes.includes('committee') && selectedCommittees.length > 0) {
          const ids = selectedCommittees.map(c => c.id).filter(Boolean).join(',');
          if (ids) {
            const members = await axios
              .get(`${API_BASE}/legislators/public/committee-members`, { params: { committee_ids: ids } })
              .then(r => (r.data.data?.members || []).map((m: any) => ({
                id: m.id,
                name: m.name,
                email: m.email,
                source: 'committee',
                chamber: m.chamber,
                district: m.district,
                party: m.party,
                committee_name: m.committee_name
              })))
              .catch(() => []);
            members.forEach(addRecipient);
          }
        }

        // Candidates from related entities
        if (recipientTypes.includes('candidate')) {
          const candidateLinks = links.filter(l => l.entity_type === 'candidate');
          const results = await Promise.all(
            candidateLinks.map(cl =>
              axios.get(`${API_BASE}/candidates/${cl.entity_id}/public-contact`)
                .then(r => {
                  const d = r.data.data;
                  if (d?.email) return [{ id: d.id, name: d.name, email: d.email, source: 'candidate' }];
                  return [];
                })
                .catch(() => [])
            )
          );
          results.flat().forEach(addRecipient);
        }

        // Custom recipients (pre-resolved at creation time)
        if (recipientTypes.includes('custom') && contactConfig.custom_recipients) {
          contactConfig.custom_recipients.forEach(cr => {
            addRecipient({
              id: cr.id,
              name: cr.name,
              email: cr.email,
              source: 'custom'
            });
          });
        }
      } catch (error) {
        console.error('Failed to resolve recipients:', error);
      } finally {
        setRecipients(allRecipients);
        setLoading(false);
      }
    };

    resolveRecipients();
  }, [demographics.district, contactConfig.enabled, recipientTypes, links, contactConfig.custom_recipients, selectedCommittees]);

  // Populate template sections when recipients change
  useEffect(() => {
    if (recipients.length === 0) return;
    const vars = buildTemplateVars(recipients, demographics, links, answers, questions);

    if (useStructured && contactConfig.email_template) {
      const et = contactConfig.email_template;
      setSubjectText(processTemplate(et.subject.content, vars));
      setOpeningText(processTemplate(et.opening.content, vars));
      setClosingText(processTemplate(et.closing.content, vars));

      if (et.included_question_ids?.length && answers && questions) {
        setQuestionAnswersText(formatQuestionAnswers(et.included_question_ids, answers, questions));
      } else {
        setQuestionAnswersText('');
      }
    } else if (contactConfig.message_template) {
      setLegacyMessage(processTemplate(contactConfig.message_template, vars));
      setLegacySubject(processTemplate(
        contactConfig.subject_template || 'Message from Constituent', vars
      ));
    }
  }, [recipients, contactConfig, demographics, links, answers, questions, useStructured]);

  const allEmails = recipients.map(r => r.email).filter(Boolean).join(',');

  // 'bcc' (default, also for absent field) hides recipients from each other;
  // in that mode the respondent's own email goes in To: so clients that
  // reject BCC-only mail still send (D1)
  const recipientVisibility = contactConfig.recipient_visibility === 'to' ? 'to' : 'bcc';
  const respondentEmail = (String(demographics.email ?? '')).trim();
  const toEmails = recipientVisibility === 'to' ? allEmails : respondentEmail;
  const bccEmails = recipientVisibility === 'to' ? '' : allEmails;

  const respondentInfoText = useMemo(() => {
    if (!contactConfig.include_respondent_info) return '';
    const fields = contactConfig.respondent_info_fields?.length
      ? contactConfig.respondent_info_fields
      : DEFAULT_RESPONDENT_INFO_FIELDS;
    return formatRespondentInfo(fields, demographics);
  }, [contactConfig.include_respondent_info, contactConfig.respondent_info_fields, demographics]);

  const composedSubject = useStructured ? subjectText : legacySubject;
  const composedBody = useStructured
    ? [openingText, questionAnswersText, respondentInfoText, freeText, closingText].filter(Boolean).join('\n\n')
    : legacyMessage;

  const logContactEvents = () => {
    if (isTest) return; // test sessions never log engagement events
    Promise.all(
      recipients.filter(r => r.email).map(r =>
        axios.post(`${API_BASE}/outreach-unified/items/${itemId}/events`, {
          event_type: 'legislator_contact',
          session_id: sessionId,
          metadata: { recipient_id: r.id, recipient_name: r.name, source: r.source }
        })
      )
    ).catch((error) => { console.error('Failed to log contact event:', error); });
  };

  // Persist the personal message when the respondent acts on it. Fire-and-
  // forget: a save failure must never block or delay the mail-client handoff.
  // Last write wins; re-saves only when the text changed since the last
  // successful save. No response row (inline contact-only card) → no save.
  const lastSavedMessage = useRef<string | null>(null);
  const savePersonalMessage = () => {
    const message = freeText.trim();
    if (!responseId || !slug || !message || message === lastSavedMessage.current) return;
    axios.post(`${API_BASE}/outreach-unified/public/${slug}/responses/${responseId}/personal-message`, { message })
      .then(() => { lastSavedMessage.current = message; })
      .catch((error) => { console.error('Failed to save personal message:', error); });
  };

  const handleOpenGmail = () => {
    if (!allEmails) return;
    const params = new URLSearchParams({ view: 'cm' });
    if (toEmails) params.set('to', toEmails);
    if (bccEmails) params.set('bcc', bccEmails);
    params.set('su', composedSubject);
    params.set('body', composedBody);
    window.open(`https://mail.google.com/mail/?${params.toString()}`, '_blank');
    setSendMethod('gmail');
    toast.success('Gmail compose opened in a new tab');
    logContactEvents();
    savePersonalMessage();
  };

  const handleMailto = () => {
    if (!allEmails) return;
    const query: string[] = [];
    if (bccEmails) query.push(`bcc=${encodeURIComponent(bccEmails)}`);
    query.push(`subject=${encodeURIComponent(composedSubject)}`);
    query.push(`body=${encodeURIComponent(composedBody)}`);
    const mailtoUrl = `mailto:${toEmails ? encodeURIComponent(toEmails) : ''}?${query.join('&')}`;
    window.location.href = mailtoUrl;
    setSendMethod('mailto');
    toast.success('Email client opened!');
    logContactEvents();
    savePersonalMessage();
  };

  const handleCopyMessage = async () => {
    const headerLines = [
      toEmails ? `To: ${toEmails}` : null,
      bccEmails ? `BCC: ${bccEmails}` : null,
      `Subject: ${composedSubject}`
    ].filter(Boolean).join('\n');
    const clipboardText = `${headerLines}\n\n${composedBody}`;
    try {
      await navigator.clipboard.writeText(clipboardText);
      setSendMethod('copied');
      toast.success('Message copied to clipboard');
      logContactEvents();
      savePersonalMessage();
    } catch {
      toast.error('Failed to copy — please try again');
    }
  };

  if (!contactConfig.enabled || loading) {
    if (loading) {
      return (
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: primaryColor }} />
          <p className="text-text-tertiary dark:text-text-dark-tertiary mt-2">Finding your recipients...</p>
        </div>
      );
    }
    return null;
  }

  if (recipients.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 text-center">
        <MapPin className="w-8 h-8 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-2" />
        <p className="text-text-tertiary dark:text-text-dark-tertiary">
          Unable to determine recipients. Please ensure your information is complete.
        </p>
      </div>
    );
  }

  if (sendMethod) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <h3 className="font-semibold text-text-primary dark:text-text-dark-primary mb-1">
          {sendMethod === 'copied' ? 'Message Copied' : 'Message Prepared'}
        </h3>
        <p className="text-text-tertiary dark:text-text-dark-tertiary text-sm mb-4">
          {sendMethod === 'gmail' && 'A Gmail compose window should have opened in a new tab.'}
          {sendMethod === 'mailto' && 'Your email client should have opened with the pre-filled message.'}
          {sendMethod === 'copied' && 'Paste the message into your email client to send it.'}
        </p>
        <button
          onClick={() => setSendMethod(null)}
          className="text-sm flex items-center gap-1 mx-auto hover:opacity-80"
          style={{ color: primaryColor }}
        >
          <ArrowLeft className="w-4 h-4" />
          Didn&apos;t work? Try another way
        </button>
      </div>
    );
  }

  // Group recipients by source for display
  const recipientsBySource = recipients.reduce<Record<string, Recipient[]>>((acc, r) => {
    if (!acc[r.source]) acc[r.source] = [];
    acc[r.source].push(r);
    return acc;
  }, {});

  const et = contactConfig.email_template;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b" style={{ backgroundColor: `${primaryColor}12` }}>
        <h3 className="font-semibold flex items-center gap-2 text-text-primary dark:text-text-dark-primary">
          <Mail className="w-5 h-5" style={{ color: primaryColor }} />
          {title || 'Contact Decision-Makers'}
        </h3>
        {!title && (
          <p className="text-sm mt-1 text-text-secondary dark:text-text-dark-secondary">
            Your voice matters! Send a message to {recipients.length === 1 ? 'the recipient' : `${recipients.length} recipients`}.
          </p>
        )}
      </div>

      {/* Recipients Display */}
      <div className="p-4 border-b space-y-3">
        {Object.entries(recipientsBySource).map(([source, recs]) => {
          const sourceInfo = SOURCE_LABELS[source] || { label: 'Recipients', icon: User };
          const SourceIcon = sourceInfo.icon;
          return (
            <div key={source}>
              <div className="flex items-center gap-2 mb-1.5">
                <SourceIcon className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider">
                  {sourceInfo.label}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 pl-6">
                {recs.map(r => (
                  <div
                    key={`${r.source}-${r.id}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary text-sm"
                  >
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">{r.name}</span>
                    {r.chamber && (
                      <span className="text-text-tertiary dark:text-text-dark-tertiary text-xs">
                        {r.chamber}{r.district ? ` ${padDistrictNumber(r.district)}` : ''}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Email Sections */}
      <div className="p-4 space-y-4">
        <label className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary block">
          Your Message
        </label>

        {useStructured && et ? (
          <>
            {/* Subject — template sections render read-only here; the respondent's
                mail client makes the final message fully editable regardless */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary">Subject</span>
              </div>
              <div className="px-3 py-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-sm text-text-secondary dark:text-text-dark-secondary">
                {subjectText}
              </div>
            </div>

            {/* Opening */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary">Opening</span>
              </div>
              <div className="px-3 py-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-wrap">
                {openingText}
              </div>
            </div>

            {/* Question Answers */}
            {questionAnswersText && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary">Your Responses</span>
                  <Lock className="w-3 h-3 text-text-tertiary dark:text-text-dark-tertiary" />
                </div>
                <div className="px-3 py-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-wrap">
                  {questionAnswersText}
                </div>
              </div>
            )}

            {/* About the respondent — shown so the respondent sees exactly what is shared */}
            {respondentInfoText && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary">Your Details</span>
                  <Lock className="w-3 h-3 text-text-tertiary dark:text-text-dark-tertiary" />
                </div>
                <div className="px-3 py-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-wrap">
                  {respondentInfoText}
                </div>
              </div>
            )}

            {/* Free Text */}
            {et.free_text_enabled && (
              <div>
                <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary block mb-1">Your Personal Message</span>
                <textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  placeholder={et.free_text_placeholder || 'Add your personal message here...'}
                  className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  aria-describedby={responseId ? 'personal-message-disclosure' : undefined}
                />
                {responseId && (
                  <p id="personal-message-disclosure" className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                    Your personal message will be shared with {organizationName || 'the organization'} along with your survey response.
                  </p>
                )}
              </div>
            )}

            {/* Closing */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary">Closing</span>
              </div>
              <div className="px-3 py-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-wrap">
                {closingText}
              </div>
            </div>
          </>
        ) : (
          /* Legacy single-message view */
          <>
            <div className="flex items-center justify-end -mt-2 mb-1">
              <button
                onClick={() => setShowLegacyEditor(!showLegacyEditor)}
                className="text-sm flex items-center gap-1 hover:opacity-80"
                style={{ color: primaryColor }}
              >
                <EditIcon className="w-4 h-4" />
                {showLegacyEditor ? 'Preview' : 'Edit'}
              </button>
            </div>
            {showLegacyEditor ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-text-tertiary dark:text-text-dark-tertiary mb-1">Subject</label>
                  <input
                    type="text"
                    value={legacySubject}
                    onChange={(e) => setLegacySubject(e.target.value)}
                    className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-tertiary dark:text-text-dark-tertiary mb-1">Message</label>
                  <textarea
                    value={legacyMessage}
                    onChange={(e) => setLegacyMessage(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-4 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                <div className="font-medium text-text-secondary dark:text-text-dark-secondary mb-2">Subject: {legacySubject}</div>
                <div className="text-text-secondary dark:text-text-dark-secondary">{legacyMessage}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 bg-background-tertiary dark:bg-background-dark-tertiary border-t border-border-light dark:border-border-dark space-y-3">
        <button
          onClick={handleOpenGmail}
          disabled={!allEmails}
          className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: primaryColor }}
        >
          <Send className="w-5 h-5" />
          Open in Gmail
          <ExternalLink className="w-4 h-4" />
        </button>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handleMailto}
            disabled={!allEmails}
            className="text-sm flex items-center gap-1.5 hover:opacity-80 disabled:opacity-50"
            style={{ color: primaryColor }}
          >
            <Mail className="w-4 h-4" />
            Other email app
          </button>
          <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">|</span>
          <button
            onClick={handleCopyMessage}
            className="text-sm flex items-center gap-1.5 hover:opacity-80"
            style={{ color: primaryColor }}
          >
            <Copy className="w-4 h-4" />
            Copy message
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipientContactCard;
